import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const categories = ['Alle', 'Handwerk', 'Gastronomie', 'Dienstleistung', 'E-Commerce'];

const projects = [
  {
    id: 1,
    title: 'Schreinerei Waldmann',
    category: 'Handwerk',
    description: 'Moderne Webseite für eine traditionelle Schreinerei im Schwarzwald. Mit Online-Anfrage-Formular und Galerie.',
    tags: ['Responsive Design', 'SEO', 'Kontaktformular'],
    image: 'https://public.readdy.ai/ai/img_res/bdcb2369858df2b72af86db67dcc9103.jpg',
    result: '+180% mehr Anfragen',
    duration: '5 Tage',
    color: 'from-amber-50 to-orange-50',
  },
  {
    id: 2,
    title: 'Restaurant Seeblick',
    category: 'Gastronomie',
    description: 'Elegante Restaurant-Webseite mit Online-Reservierung, Speisekarte und Bildergalerie für ein Seerestaurant.',
    tags: ['Reservierungssystem', 'Galerie', 'DSGVO'],
    image: 'https://readdy.ai/api/search-image?query=elegant%20restaurant%20website%20design%20mockup%20on%20laptop%20screen%2C%20lake%20view%20restaurant%20with%20beautiful%20food%20photography%2C%20clean%20modern%20layout%20with%20dark%20green%20and%20cream%20colors%2C%20professional%20web%20design%20portfolio%20screenshot&width=800&height=520&seq=portfolio-restaurant-v1&orientation=landscape',
    result: '+240% Online-Reservierungen',
    duration: '4 Tage',
    color: 'from-emerald-50 to-teal-50',
  },
  {
    id: 3,
    title: 'Steuerberatung Müller',
    category: 'Dienstleistung',
    description: 'Seriöse und vertrauenswürdige Webseite für eine Steuerberatungskanzlei mit Terminbuchung und AI-Chatbot.',
    tags: ['AI-Chatbot', 'Terminbuchung', 'SSL'],
    image: 'https://readdy.ai/api/search-image?query=professional%20tax%20consulting%20firm%20website%20design%20on%20laptop%20screen%2C%20clean%20corporate%20layout%20with%20navy%20blue%20and%20white%20colors%2C%20trust%20and%20credibility%20focused%20design%2C%20modern%20business%20web%20design%20portfolio%20mockup&width=800&height=520&seq=portfolio-steuer-v1&orientation=landscape',
    result: '+95% mehr Terminanfragen',
    duration: '6 Tage',
    color: 'from-slate-50 to-blue-50',
  },
  {
    id: 4,
    title: 'Naturkosmetik Waldblüte',
    category: 'E-Commerce',
    description: 'Hochwertiger Online-Shop für natürliche Kosmetikprodukte aus dem Schwarzwald. Mit Produktkatalog und Warenkorb.',
    tags: ['Online-Shop', 'Produktkatalog', 'SEO'],
    image: 'https://readdy.ai/api/search-image?query=natural%20cosmetics%20e-commerce%20website%20design%20on%20laptop%20screen%2C%20organic%20beauty%20products%20from%20black%20forest%20germany%2C%20clean%20minimal%20layout%20with%20sage%20green%20and%20cream%20tones%2C%20elegant%20product%20photography%20web%20design%20mockup&width=800&height=520&seq=portfolio-kosmetik-v1&orientation=landscape',
    result: '+320% Online-Umsatz',
    duration: '7 Tage',
    color: 'from-lime-50 to-green-50',
  },
  {
    id: 5,
    title: 'Physiotherapie Körperwohl',
    category: 'Dienstleistung',
    description: 'Professionelle Praxis-Webseite mit Online-Terminbuchung, Leistungsübersicht und AI-Chatbot für Patientenanfragen.',
    tags: ['AI-Chatbot', 'Terminbuchung', 'Responsive'],
    image: 'https://readdy.ai/api/search-image?query=physiotherapy%20practice%20website%20design%20on%20laptop%20screen%2C%20calm%20healing%20atmosphere%20with%20soft%20beige%20and%20green%20colors%2C%20appointment%20booking%20interface%2C%20professional%20health%20services%20web%20design%20portfolio%20mockup&width=800&height=520&seq=portfolio-physio-v1&orientation=landscape',
    result: '+150% Terminbuchungen',
    duration: '5 Tage',
    color: 'from-rose-50 to-pink-50',
  },
  {
    id: 6,
    title: 'Elektro Brenner GmbH',
    category: 'Handwerk',
    description: 'Übersichtliche Unternehmenswebseite für einen Elektrobetrieb mit Leistungsseiten, Referenzen und Notfallkontakt.',
    tags: ['Responsive Design', 'SEO', 'DSGVO'],
    image: 'https://readdy.ai/api/search-image?query=electrical%20services%20company%20website%20design%20on%20laptop%20screen%2C%20professional%20electrician%20business%20with%20yellow%20and%20dark%20gray%20color%20scheme%2C%20clean%20corporate%20layout%20with%20service%20icons%2C%20modern%20trade%20business%20web%20design%20portfolio%20mockup&width=800&height=520&seq=portfolio-elektro-v1&orientation=landscape',
    result: '+200% Sichtbarkeit Google',
    duration: '4 Tage',
    color: 'from-yellow-50 to-amber-50',
  },
];

export default function PortfolioGrid() {
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const filtered = activeCategory === 'Alle'
    ? projects
    : projects.filter((p) => p.category === activeCategory);

  return (
    <section className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">

        {/* Section intro */}
        <div className="text-center mb-10">
          <p className="text-sm text-brand-grey max-w-xl mx-auto">
            Die folgenden Projekte zeigen, <span className="font-semibold text-brand-black">was ich für dich umsetzen kann</span> – 
            Designs, Funktionen und Ergebnisse, die ich für echte Kundenprojekte anstrebe.
            Alle Karten sind mit <span className="font-semibold text-amber-600">„Beispielprojekt"</span> gekennzeichnet.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-12">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-brand-green text-white shadow-md'
                  : 'bg-brand-beige-light text-brand-grey hover:bg-brand-beige hover:text-brand-black'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {filtered.map((project, index) => (
              <motion.div
                key={project.id}
                layout
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, delay: index * 0.07 }}
                onMouseEnter={() => setHoveredId(project.id)}
                onMouseLeave={() => setHoveredId(null)}
                className="group bg-white rounded-2xl border border-brand-beige overflow-hidden hover:border-brand-green hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    decoding="async"
                    width={800}
                    height={520}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  {/* Category badge */}
                  <div className="absolute top-3 right-3">
                    <span className="bg-white/90 backdrop-blur-sm text-brand-green text-xs font-semibold px-3 py-1 rounded-full">
                      {project.category}
                    </span>
                  </div>

                  {/* Beispielprojekt badge */}
                  <div className="absolute top-3 left-3">
                    <span className="bg-amber-400/90 backdrop-blur-sm text-amber-900 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                      <i className="ri-flask-line text-xs"></i>
                      Beispielprojekt
                    </span>
                  </div>

                  {/* Result Badge on hover */}
                  <div
                    className={`absolute bottom-3 left-3 transition-all duration-300 ${
                      hoveredId === project.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                    }`}
                  >
                    <span className="bg-brand-green text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                      <i className="ri-arrow-up-line text-sm"></i>
                      Ziel: {project.result}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-caprasimo text-brand-black mb-2 group-hover:text-brand-green transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-sm text-brand-grey leading-relaxed mb-4">
                    {project.description}
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-brand-beige-light text-brand-grey px-2.5 py-1 rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t border-brand-beige">
                    <div className="flex items-center gap-1.5 text-xs text-brand-grey">
                      <i className="ri-time-line text-brand-green"></i>
                      Fertig in {project.duration}
                    </div>
                    <div className="flex items-center gap-1 text-xs font-semibold text-brand-green group-hover:gap-2 transition-all">
                      Mehr erfahren
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-16 text-center bg-brand-beige-light rounded-2xl px-6 py-10 border border-brand-beige"
        >
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-4 bg-brand-green/10 rounded-full">
            <i className="ri-camera-line text-brand-green text-xl"></i>
          </div>
          <h3 className="text-xl font-caprasimo text-brand-black mb-2">
            Dein Projekt könnte hier stehen
          </h3>
          <p className="text-sm text-brand-grey max-w-md mx-auto mb-6">
            Ich suche meine ersten Kunden und biete dafür besonders attraktive Konditionen.
            Werde Teil meines Portfolios – mit echtem Screenshot und echter Erfolgsgeschichte.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-brand-green text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-brand-green/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Jetzt Erstgespräch vereinbaren
          </a>
        </motion.div>

      </div>
    </section>
  );
}
